import React, { useState, useEffect, FormEvent } from 'react';
import { apiClient, API_BASE } from '../api/client';
import { usePreferences } from '../contexts/PreferencesContext';
import { useAuth } from '../contexts/AuthContext';
import { isAtLeast } from '../utils/roles';
import { usePageAuthorization } from '../hooks/usePageAuthorization';

// ── Type Definitions ──────────────────────────────────────────

interface Vendor {
  id: string;
  businessName: string;
  contactPerson: string;
  email: string;
  phone: string | null;
  mailingAddress: string | null;
  category: string | null;
  createdAt: string;
  updatedAt: string;
  eventRegistrations?: VendorListRegistration[];
}

interface VendorListRegistration {
  id: string;
  status: string;
  boothNumber: string | null;
  zone: string | null;
  event: { name: string };
}

interface VendorDetail extends Vendor {
  eventRegistrations: VendorEventRegistration[];
}

interface VendorEventRegistration {
  id: string;
  vendorId: string;
  eventId: string;
  registrationDate: string;
  status: 'Pending' | 'Confirmed' | 'Cancelled';
  event: { id: string; name: string; date: string };
  payments: VendorPayment[];
  boothNumber: string | null;
  zone: string | null;
  description: string | null;
}

interface VendorPayment {
  id: string;
  registrationId: string;
  amount: number;
  paymentDate: string;
  paymentMethod: PaymentMethod;
  registration?: { event: { id: string; name: string } };
}

type PaymentMethod = 'Cash' | 'Check' | 'CreditCard' | 'BankTransfer' | 'PayPal' | 'Zelle' | 'Other';

type VendorCategory = 'Food' | 'Merchandise' | 'Services' | 'Entertainment' | 'Health & Wellness' | 'Arts & Crafts' | 'Other';

interface RevenueResponse {
  totalRevenue: number;
}

interface EventOption {
  id: string;
  name: string;
  date: string;
}

// ── Constants ─────────────────────────────────────────────────

const VENDOR_CATEGORIES: VendorCategory[] = [
  'Food', 'Merchandise', 'Services', 'Entertainment',
  'Health & Wellness', 'Arts & Crafts', 'Other',
];

const PAYMENT_METHODS: PaymentMethod[] = [
  'Cash', 'Check', 'CreditCard', 'BankTransfer', 'PayPal', 'Zelle', 'Other',
];

// ── Inline Styles ─────────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
  page: { padding: '2rem', maxWidth: '1200px', margin: '0 auto' },
  title: { color: '#1a6b3c', fontSize: '1.75rem', margin: '0 0 1.5rem 0' },
  section: { marginBottom: '2rem', padding: '1.5rem', backgroundColor: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' },
  sectionTitle: { color: '#0f2d1f', fontSize: '1.2rem', marginBottom: '1rem', borderBottom: '2px solid #c9a84c', paddingBottom: '0.25rem' },
  btn: { padding: '0.5rem 1rem', backgroundColor: '#1a6b3c', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', marginRight: '0.5rem' },
  btnDanger: { padding: '0.5rem 1rem', backgroundColor: '#d32f2f', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', marginRight: '0.5rem' },
  btnGold: { padding: '0.5rem 1rem', backgroundColor: '#c9a84c', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' },
  input: { padding: '0.5rem 0.75rem', border: '1px solid #ccc', borderRadius: '4px', fontSize: '0.9rem', boxSizing: 'border-box' as const },
  select: { padding: '0.5rem 0.75rem', border: '1px solid #ccc', borderRadius: '4px', fontSize: '0.9rem', backgroundColor: '#fff', boxSizing: 'border-box' as const },
  error: { color: '#d32f2f', backgroundColor: '#fdecea', padding: '0.75rem 1rem', borderRadius: '4px', marginBottom: '1rem', fontSize: '0.85rem', cursor: 'pointer' },
  field: { marginBottom: '1rem' },
  label: { display: 'block', marginBottom: '0.25rem', fontWeight: 600, fontSize: '0.85rem', color: '#333' },
  formGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' },
  toggleActive: { padding: '0.4rem 0.75rem', backgroundColor: '#1a6b3c', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', marginLeft: '0.5rem' },
  toggleInactive: { padding: '0.4rem 0.75rem', backgroundColor: '#e0e0e0', color: '#333', border: 'none', borderRadius: '4px', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', marginLeft: '0.5rem' },
  tableWrapper: { overflowX: 'auto' as const },
  table: { width: '100%', borderCollapse: 'collapse' as const, fontSize: '0.9rem' },
  th: { textAlign: 'left' as const, padding: '0.75rem 0.5rem', borderBottom: '2px solid #e0e0e0', fontWeight: 600, color: '#0f2d1f', fontSize: '0.85rem' },
  td: { padding: '0.75rem 0.5rem', borderBottom: '1px solid #e0e0e0', fontSize: '0.85rem', color: '#333' },
  trClickable: { cursor: 'pointer', transition: 'background-color 0.15s' },
  trExpanded: { backgroundColor: '#f0f7f2' },
  card: { padding: '1rem', border: '1px solid #e0e0e0', borderRadius: '6px', marginBottom: '0.75rem', backgroundColor: '#fafafa', cursor: 'pointer', transition: 'background-color 0.15s' },
  cardExpanded: { backgroundColor: '#f0f7f2', borderColor: '#1a6b3c' },
  detailPanel: { padding: '1.5rem', backgroundColor: '#f9f9f9', borderRadius: '6px', marginTop: '0.5rem', border: '1px solid #e0e0e0' },
  revenueCard: { padding: '1.5rem', backgroundColor: '#0f2d1f', borderRadius: '8px', color: '#fff', marginBottom: '1.5rem', textAlign: 'center' as const },
  revenueAmount: { fontSize: '2rem', fontWeight: 700, color: '#c9a84c' },
  revenueLabel: { fontSize: '0.85rem', color: '#a3b8a9', marginTop: '0.25rem' },
};

// ── Component ─────────────────────────────────────────────────

export function VendorsPage() {

  const { canEdit } = usePageAuthorization('vendors');
  const { preferences } = usePreferences();
  const { user } = useAuth();
  const isOrganizer = isAtLeast(user?.role || '', 'Organizer');

  // Data state
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [totalRevenue, setTotalRevenue] = useState<number>(0);
  const [vendorDetail, setVendorDetail] = useState<VendorDetail | null>(null);
  const [events, setEvents] = useState<EventOption[]>([]);

  // UI state
  const [viewMode, setViewMode] = useState<'cards' | 'table'>(() =>
    preferences.defaultViewMode === 'table' ? 'table' : 'cards'
  );
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [eventFilter, setEventFilter] = useState<string>('');
  const [expandedVendorId, setExpandedVendorId] = useState<string | null>(null);
  const [showOnboardingForm, setShowOnboardingForm] = useState(false);
  const [showRegistrationForm, setShowRegistrationForm] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [error, setError] = useState<string>('');
  const [detailLoading, setDetailLoading] = useState(false);

  // Onboarding form fields
  const [formBusinessName, setFormBusinessName] = useState('');
  const [formContactPerson, setFormContactPerson] = useState('');
  const [formEmail, setFormEmail] = useState('');
  const [formPhone, setFormPhone] = useState('');
  const [formMailingAddress, setFormMailingAddress] = useState('');
  const [formCategory, setFormCategory] = useState<string>('');

  // Edit fields (populated from vendorDetail)
  const [editBusinessName, setEditBusinessName] = useState('');
  const [editContactPerson, setEditContactPerson] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [editMailingAddress, setEditMailingAddress] = useState('');
  const [editCategory, setEditCategory] = useState('');

  // Registration form
  const [regEventId, setRegEventId] = useState('');

  // Payment form
  const [payRegistrationId, setPayRegistrationId] = useState('');
  const [payAmount, setPayAmount] = useState('');
  const [payDate, setPayDate] = useState('');
  const [payMethod, setPayMethod] = useState<PaymentMethod>('Cash');

  // Booth location editing state (keyed by registration id)
  const [boothEdits, setBoothEdits] = useState<Record<string, { boothNumber: string; zone: string; description: string }>>({});
  const [boothSaving, setBoothSaving] = useState<string | null>(null);

  // Catalog items state
  const [catalogItems, setCatalogItems] = useState<{ id: string; name: string; description: string | null; price: number | null; imageUrl: string | null; category: string | null; tags: string | null; featured: boolean }[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [showCatalogForm, setShowCatalogForm] = useState(false);
  const [catalogEditId, setCatalogEditId] = useState<string | null>(null);
  const [catName, setCatName] = useState('');
  const [catDescription, setCatDescription] = useState('');
  const [catPrice, setCatPrice] = useState('');
  const [catCategory, setCatCategory] = useState('');
  const [catTags, setCatTags] = useState('');
  const [catFeatured, setCatFeatured] = useState(false);
  const [catImageUrl, setCatImageUrl] = useState('');
  const [catVideoUrl, setCatVideoUrl] = useState('');
  const [catImageUploading, setCatImageUploading] = useState(false);
  const [catalogViewMode, setCatalogViewMode] = useState<'cards' | 'table'>('cards');

  // ── Data Fetching ─────────────────────────────────────────────

  async function fetchVendors() {
    try {
      const params = new URLSearchParams();
      if (categoryFilter) params.set('category', categoryFilter);
      if (eventFilter) params.set('eventId', eventFilter);
      const url = params.toString() ? `/vendors?${params.toString()}` : '/vendors';
      const data = await apiClient<Vendor[]>(url);
      setVendors(data);
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message || 'Failed to load vendors.');
    }
  }

  async function fetchRevenue() {
    try {
      const data = await apiClient<RevenueResponse>('/vendors/revenue/total');
      setTotalRevenue(data.totalRevenue);
    } catch {
      setTotalRevenue(0);
    }
  }

  async function fetchVendorDetail(vendorId: string) {
    setDetailLoading(true);
    try {
      const data = await apiClient<VendorDetail>(`/vendors/${vendorId}`);
      setVendorDetail(data);
      // Populate edit fields
      setEditBusinessName(data.businessName);
      setEditContactPerson(data.contactPerson);
      setEditEmail(data.email);
      setEditPhone(data.phone || '');
      setEditMailingAddress(data.mailingAddress || '');
      setEditCategory(data.category || '');
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message || 'Failed to load vendor details.');
    } finally {
      setDetailLoading(false);
    }
  }

  async function fetchEvents() {
    try {
      const data = await apiClient<EventOption[]>('/events');
      setEvents(data);
    } catch (err) {
      console.error('[VendorsPage] Failed to fetch events:', err);
    }
  }

  async function fetchCatalogItems(vendorId: string) {
    setCatalogLoading(true);
    try {
      const data = await apiClient<typeof catalogItems>(`/vendor-catalog/vendors/${vendorId}/items`);
      setCatalogItems(data);
    } catch {
      setCatalogItems([]);
    } finally {
      setCatalogLoading(false);
    }
  }

  function resetCatalogForm() {
    setCatName(''); setCatDescription(''); setCatPrice(''); setCatCategory('');
    setCatTags(''); setCatFeatured(false); setCatImageUrl(''); setCatVideoUrl(''); setCatalogEditId(null);
    setShowCatalogForm(false);
  }

  function startEditCatalogItem(item: typeof catalogItems[0]) {
    setCatalogEditId(item.id);
    setCatName(item.name);
    setCatDescription(item.description || '');
    setCatPrice(item.price !== null ? String(item.price) : '');
    setCatCategory(item.category || '');
    setCatTags(item.tags || '');
    setCatFeatured(item.featured);
    setCatImageUrl(item.imageUrl || '');
    setCatVideoUrl((item as any).videoUrl || '');
    setShowCatalogForm(true);
  }

  async function handleCatalogImageUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    // Client-side 5MB limit
    if (file.size > 5 * 1024 * 1024) {
      setError('File too large. Maximum size is 5MB.');
      return;
    }
    setCatImageUploading(true);
    try {
      const { uploadFile } = await import('../api/client');
      const result = await uploadFile<{ imageUrl?: string; error?: string }>('/vendor-catalog/upload-image', file);
      if (result.error) {
        setError(result.error);
      } else if (result.imageUrl) {
        setCatImageUrl(result.imageUrl);
      }
    } catch (err: any) {
      setError(err?.message || 'Image upload failed');
    } finally {
      setCatImageUploading(false);
    }
  }

  async function handleSaveCatalogItem(e: React.FormEvent) {
    e.preventDefault();
    if (!catName.trim()) return;
    const vendorId = vendorDetail?.id;
    if (!vendorId) return;
    const body: any = {
      name: catName.trim(),
      description: catDescription.trim() || undefined,
      price: catPrice ? parseFloat(catPrice) : undefined,
      category: catCategory || undefined,
      tags: catTags.trim() || undefined,
      featured: catFeatured,
      imageUrl: catImageUrl || undefined,
      videoUrl: catVideoUrl.trim() || undefined,
    };
    try {
      if (catalogEditId) {
        await apiClient(`/vendor-catalog/items/${catalogEditId}`, { method: 'PATCH', body });
      } else {
        await apiClient(`/vendor-catalog/vendors/${vendorId}/items`, { method: 'POST', body });
      }
      resetCatalogForm();
      fetchCatalogItems(vendorId);
    } catch (err: any) {
      setError(err?.message || 'Failed to save catalog item');
    }
  }

  async function handleDeleteCatalogItem(itemId: string) {
    if (!confirm('Delete this catalog item?')) return;
    try {
      await apiClient(`/vendor-catalog/items/${itemId}`, { method: 'DELETE' });
      if (vendorDetail) fetchCatalogItems(vendorDetail.id);
    } catch (err: any) {
      setError(err?.message || 'Failed to delete catalog item');
    }
  }

  useEffect(() => {
    fetchVendors();
    fetchRevenue();
    fetchEvents();
  }, []);

  useEffect(() => {
    fetchVendors();
  }, [categoryFilter, eventFilter]);

  // ── Vendor Actions ────────────────────────────────────────────

  async function handleCreateVendor(e: FormEvent) {
    e.preventDefault();
    if (!formBusinessName.trim() || !formContactPerson.trim() || !formEmail.trim()) {
      setError('Business name, contact person, and email are required.');
      return;
    }
    try {
      await apiClient('/vendors', {
        method: 'POST',
        body: {
          businessName: formBusinessName.trim(),
          contactPerson: formContactPerson.trim(),
          email: formEmail.trim(),
          ...(formPhone.trim() && { phone: formPhone.trim() }),
          ...(formMailingAddress.trim() && { mailingAddress: formMailingAddress.trim() }),
          ...(formCategory && { category: formCategory }),
        },
      });
      // Reset form
      setFormBusinessName('');
      setFormContactPerson('');
      setFormEmail('');
      setFormPhone('');
      setFormMailingAddress('');
      setFormCategory('');
      setShowOnboardingForm(false);
      setError('');
      fetchVendors();
      fetchRevenue();
    } catch (err: unknown) {
      const apiErr = err as { message?: string; statusCode?: number };
      setError(apiErr.message || 'Failed to create vendor.');
    }
  }

  async function handleSaveVendor() {
    if (!vendorDetail) return;
    try {
      await apiClient(`/vendors/${vendorDetail.id}`, {
        method: 'PATCH',
        body: {
          businessName: editBusinessName.trim(),
          contactPerson: editContactPerson.trim(),
          email: editEmail.trim(),
          phone: editPhone.trim() || null,
          mailingAddress: editMailingAddress.trim() || null,
          category: editCategory || null,
        },
      });
      setError('');
      fetchVendors();
      fetchVendorDetail(vendorDetail.id);
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message || 'Failed to update vendor.');
    }
  }

  async function handleDeleteVendor() {
    if (!vendorDetail) return;
    if (!confirm(`Are you sure you want to delete "${vendorDetail.businessName}"?`)) return;
    try {
      await apiClient(`/vendors/${vendorDetail.id}`, { method: 'DELETE' });
      setExpandedVendorId(null);
      setVendorDetail(null);
      setError('');
      fetchVendors();
      fetchRevenue();
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message || 'Failed to delete vendor.');
    }
  }

  // ── Event Registration Actions ────────────────────────────────

  async function handleAddRegistration(e: FormEvent) {
    e.preventDefault();
    if (!vendorDetail || !regEventId) return;
    try {
      await apiClient(`/vendors/${vendorDetail.id}/event-registrations`, {
        method: 'POST',
        body: { eventId: regEventId },
      });
      setRegEventId('');
      setShowRegistrationForm(false);
      setError('');
      fetchVendorDetail(vendorDetail.id);
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message || 'Failed to register for event.');
    }
  }

  async function handleUpdateRegistrationStatus(regId: string, status: string) {
    if (!vendorDetail) return;
    try {
      await apiClient(`/vendors/${vendorDetail.id}/event-registrations/${regId}`, {
        method: 'PATCH',
        body: { status },
      });
      setError('');
      fetchVendorDetail(vendorDetail.id);
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message || 'Failed to update registration status.');
    }
  }

  // ── Payment Actions ───────────────────────────────────────────

  async function handleAddPayment(e: FormEvent) {
    e.preventDefault();
    if (!vendorDetail || !payRegistrationId || !payAmount || !payDate) return;
    try {
      await apiClient(`/vendors/${vendorDetail.id}/payments`, {
        method: 'POST',
        body: {
          registrationId: payRegistrationId,
          amount: parseFloat(payAmount),
          paymentDate: payDate,
          paymentMethod: payMethod,
        },
      });
      setPayRegistrationId('');
      setPayAmount('');
      setPayDate('');
      setPayMethod('Cash');
      setShowPaymentForm(false);
      setError('');
      fetchVendorDetail(vendorDetail.id);
      fetchRevenue();
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message || 'Failed to record payment.');
    }
  }

  // ── Booth Location Actions ────────────────────────────────────

  function getBoothEditValues(reg: VendorEventRegistration) {
    if (boothEdits[reg.id]) return boothEdits[reg.id];
    return {
      boothNumber: reg.boothNumber || '',
      zone: reg.zone || '',
      description: reg.description || '',
    };
  }

  function updateBoothEdit(regId: string, field: 'boothNumber' | 'zone' | 'description', value: string) {
    setBoothEdits((prev) => {
      const existing = prev[regId];
      if (existing) {
        return { ...prev, [regId]: { ...existing, [field]: value } };
      }
      // Initialize from current registration values
      const reg = vendorDetail?.eventRegistrations.find((r) => r.id === regId);
      return {
        ...prev,
        [regId]: {
          boothNumber: reg?.boothNumber || '',
          zone: reg?.zone || '',
          description: reg?.description || '',
          [field]: value,
        },
      };
    });
  }

  async function handleSaveBooth(regId: string) {
    if (!vendorDetail) return;
    const values = getBoothEditValues(
      vendorDetail.eventRegistrations.find((r) => r.id === regId)!
    );
    setBoothSaving(regId);
    try {
      await apiClient(`/vendors/${vendorDetail.id}/event-registrations/${regId}/booth-location`, {
        method: 'PATCH',
        body: {
          boothNumber: values.boothNumber.trim() || null,
          zone: values.zone.trim() || null,
          description: values.description.trim() || null,
        },
      });
      setError('');
      // Clear local edits for this registration
      setBoothEdits((prev) => {
        const next = { ...prev };
        delete next[regId];
        return next;
      });
      fetchVendorDetail(vendorDetail.id);
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message || 'Failed to save booth location.');
    } finally {
      setBoothSaving(null);
    }
  }

  // ── Expansion Handler ─────────────────────────────────────────

  function handleVendorClick(vendorId: string) {
    if (expandedVendorId === vendorId) {
      setExpandedVendorId(null);
      setVendorDetail(null);
      setShowRegistrationForm(false);
      setShowPaymentForm(false);
      return;
    }
    setExpandedVendorId(vendorId);
    setShowRegistrationForm(false);
    setShowPaymentForm(false);
    setShowCatalogForm(false);
    fetchVendorDetail(vendorId);
    fetchCatalogItems(vendorId);
    fetchEvents();
  }

  // ── Helpers ───────────────────────────────────────────────────

  function formatCurrency(amount: number): string {
    return amount.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 });
  }

  // Sort vendors alphabetically by business name (case-insensitive)
  const sortedVendors = [...vendors].sort((a, b) =>
    a.businessName.toLowerCase().localeCompare(b.businessName.toLowerCase())
  );

  // Print a clean vendor list in a new window (respects current event/category filters).
  const handlePrintVendors = () => {
    const escapeHtml = (s: string) =>
      s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const rows = sortedVendors.map((v) => {
      const reg = v.eventRegistrations?.find((r) => r.boothNumber) || v.eventRegistrations?.[0];
      const booth = reg?.boothNumber ? `${reg.boothNumber}${reg.zone ? ` · ${reg.zone}` : ''}` : '—';
      return `<tr>
        <td>${escapeHtml(v.businessName || '')}</td>
        <td>${escapeHtml(v.category || '—')}</td>
        <td>${escapeHtml(v.contactPerson || '—')}</td>
        <td>${escapeHtml(v.phone || '—')}</td>
        <td>${escapeHtml(v.email || '—')}</td>
        <td>${escapeHtml(booth)}</td>
      </tr>`;
    }).join('');
    const eventLabel = eventFilter ? (events.find((e) => e.id === eventFilter)?.name || '') : 'All Events';
    const catLabel = categoryFilter || 'All Categories';
    const html = `<!DOCTYPE html><html><head>
      <title>Vendor List — KDU Georgia 2026</title>
      <style>
        body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; padding: 24px; color: #1a1a1a; }
        h1 { font-size: 18px; margin-bottom: 4px; color: #1a6b3c; }
        p { color: #666; font-size: 12px; margin-bottom: 16px; }
        table { width: 100%; border-collapse: collapse; }
        th { text-align: left; padding: 8px 10px; border-bottom: 2px solid #333; font-size: 12px; text-transform: uppercase; letter-spacing: 0.03em; }
        td { padding: 6px 10px; border-bottom: 1px solid #ddd; font-size: 12px; }
        tr:nth-child(even) td { background: #f8f8f8; }
      </style>
    </head><body>
      <h1>Vendor List — KDU Georgia 2026</h1>
      <p>${escapeHtml(eventLabel)} · ${escapeHtml(catLabel)} · ${sortedVendors.length} vendor${sortedVendors.length !== 1 ? 's' : ''} · Printed ${new Date().toLocaleDateString()}</p>
      <table>
        <thead><tr><th>Business</th><th>Category</th><th>Contact</th><th>Phone</th><th>Email</th><th>Booth</th></tr></thead>
        <tbody>${rows}</tbody>
      </table>
    </body></html>`;
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(html);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => printWindow.print(), 300);
    }
  };

  // Calculate per-vendor payment total
  const vendorPaymentTotal = vendorDetail
    ? vendorDetail.eventRegistrations.reduce(
        (sum, reg) => sum + reg.payments.reduce((pSum, p) => pSum + Number(p.amount), 0),
        0
      )
    : 0;

  // ── Desktop Detection ───────────────────────────────────────

  const [isDesktop, setIsDesktop] = useState(() => window.innerWidth >= 1024);

  useEffect(() => {
    function handleResize() {
      setIsDesktop(window.innerWidth >= 1024);
    }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ── Desktop search filter ─────────────────────────────────────
  const [vendorSearch, setVendorSearch] = useState('');

  // Filtered vendors for sidebar search
  const sidebarVendors = vendorSearch
    ? sortedVendors.filter((v) => v.businessName.toLowerCase().includes(vendorSearch.toLowerCase()))
    : sortedVendors;

  // ── Render ────────────────────────────────────────────────────

  // Desktop 3-pane layout
  if (isDesktop) {
    return (
      <div style={{ padding: '1rem 1.5rem', maxWidth: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
          <h1 style={{ ...styles.title, marginBottom: 0 }}>Vendor Management</h1>
          <button
            style={{ ...styles.btnGold, whiteSpace: 'nowrap' }}
            onClick={handlePrintVendors}
            disabled={sortedVendors.length === 0}
            title="Print the vendor list"
            aria-label="Print vendor list"
          >
            🖨️ Print Vendor List
          </button>
        </div>

        {/* Error Banner */}
        {error && (
          <div style={styles.error} onClick={() => setError('')}>
            {error} <span style={{ float: 'right', fontWeight: 700 }}>×</span>
          </div>
        )}

        {/* Revenue Summary Card */}
        <div style={styles.revenueCard}>
          <div style={styles.revenueAmount}>{formatCurrency(totalRevenue)}</div>
          <div style={styles.revenueLabel}>Total Vendor Revenue</div>
        </div>

        {/* 3-Pane Container */}
        <div style={{ display: 'flex', height: 'calc(100vh - 200px)', border: '2px solid #1a6b3c', borderRadius: '10px', overflow: 'hidden' }}>

          {/* Pane 1: Vendor List Sidebar */}
          <div style={{ width: '300px', minWidth: '300px', backgroundColor: '#f9fafb', overflowY: 'auto', display: 'flex', flexDirection: 'column', borderRight: '2px solid #e5e7eb' }}>
            <div style={{ padding: '0.75rem', borderBottom: '1px solid #e5e7eb', position: 'sticky', top: 0, backgroundColor: '#ffffff', zIndex: 1 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                <h3 style={{ margin: 0, fontSize: '0.85rem', color: '#0f2d1f', fontWeight: 700 }}>Vendors ({sortedVendors.length})</h3>
                {(canEdit || isOrganizer) && (
                  <button
                    style={{ ...styles.btn, fontSize: '0.7rem', padding: '0.25rem 0.5rem', marginRight: 0 }}
                    onClick={() => setShowOnboardingForm(!showOnboardingForm)}
                    title="Add Vendor"
                  >
                    +
                  </button>
                )}
              </div>
              <input
                style={{ ...styles.input, width: '100%', fontSize: '0.8rem', padding: '0.4rem 0.6rem' }}
                placeholder="Search vendors..."
                value={vendorSearch}
                onChange={(e) => setVendorSearch(e.target.value)}
              />
              <div style={{ display: 'flex', gap: '0.25rem', marginTop: '0.4rem' }}>
                <select
                  style={{ ...styles.select, flex: 1, fontSize: '0.7rem', padding: '0.25rem' }}
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                >
                  <option value="">All Categories</option>
                  {VENDOR_CATEGORIES.map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
            </div>
            <div style={{ flex: 1, overflowY: 'auto' }}>
              {sidebarVendors.length === 0 ? (
                <p style={{ padding: '0.75rem', color: '#6b7280', fontSize: '0.8rem' }}>No vendors found.</p>
              ) : (
                sidebarVendors.map((vendor) => (
                  <div
                    key={vendor.id}
                    onClick={() => handleVendorClick(vendor.id)}
                    style={{
                      padding: '0.6rem 0.75rem',
                      cursor: 'pointer',
                      borderBottom: '1px solid #e5e7eb',
                      borderLeft: expandedVendorId === vendor.id ? '3px solid #1a6b3c' : '3px solid transparent',
                      backgroundColor: expandedVendorId === vendor.id ? '#f0f7f2' : '#ffffff',
                      transition: 'background-color 0.15s',
                    }}
                  >
                    <div style={{ fontWeight: 600, fontSize: '0.82rem', color: '#0f2d1f', marginBottom: '0.15rem' }}>{vendor.businessName}</div>
                    <div style={{ fontSize: '0.7rem', color: '#6b7280' }}>{vendor.contactPerson}</div>
                    {vendor.category && (
                      <span style={{ display: 'inline-block', marginTop: '0.2rem', padding: '0.1rem 0.4rem', borderRadius: '3px', fontSize: '0.6rem', fontWeight: 600, color: '#fff', backgroundColor: '#1a6b3c' }}>
                        {vendor.category}
                      </span>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Pane 2: Vendor Detail (Edit Form, Registrations, Payments) */}
          <div style={{ flex: 1, minWidth: 0, backgroundColor: '#ffffff', overflowY: 'auto', padding: '0.75rem', fontSize: '0.82rem', borderRight: '2px solid #e5e7eb' }}>
            {/* Onboarding Form at top of Pane 2 when triggered */}
            {showOnboardingForm && (
              <form onSubmit={handleCreateVendor} style={{ padding: '1rem', backgroundColor: '#f9f9f9', borderRadius: '6px', marginBottom: '1rem', border: '1px solid #e0e0e0' }}>
                <h3 style={{ margin: '0 0 1rem 0', color: '#0f2d1f', fontSize: '1rem' }}>New Vendor</h3>
                <div style={styles.formGrid}>
                  <div style={styles.field}>
                    <label style={styles.label}>Business Name *</label>
                    <input style={{ ...styles.input, width: '100%' }} value={formBusinessName} onChange={(e) => setFormBusinessName(e.target.value)} />
                  </div>
                  <div style={styles.field}>
                    <label style={styles.label}>Contact Person *</label>
                    <input style={{ ...styles.input, width: '100%' }} value={formContactPerson} onChange={(e) => setFormContactPerson(e.target.value)} />
                  </div>
                  <div style={styles.field}>
                    <label style={styles.label}>Email *</label>
                    <input type="email" style={{ ...styles.input, width: '100%' }} value={formEmail} onChange={(e) => setFormEmail(e.target.value)} />
                  </div>
                  <div style={styles.field}>
                    <label style={styles.label}>Phone</label>
                    <input style={{ ...styles.input, width: '100%' }} value={formPhone} onChange={(e) => setFormPhone(e.target.value)} />
                  </div>
                  <div style={styles.field}>
                    <label style={styles.label}>Mailing Address</label>
                    <input style={{ ...styles.input, width: '100%' }} value={formMailingAddress} onChange={(e) => setFormMailingAddress(e.target.value)} />
                  </div>
                  <div style={styles.field}>
                    <label style={styles.label}>Category</label>
                    <select style={{ ...styles.select, width: '100%' }} value={formCategory} onChange={(e) => setFormCategory(e.target.value)}>
                      <option value="">Select category...</option>
                      {VENDOR_CATEGORIES.map((cat) => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div style={{ marginTop: '0.5rem' }}>
                  <button type="submit" style={styles.btn}>Create Vendor</button>
                  <button type="button" style={styles.btnGold} onClick={() => setShowOnboardingForm(false)}>Cancel</button>
                </div>
              </form>
            )}

            {!expandedVendorId ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#6b7280', fontSize: '0.95rem' }}>
                Select a vendor from the list
              </div>
            ) : detailLoading ? (
              <p style={{ color: '#6b7280' }}>Loading vendor details...</p>
            ) : !vendorDetail ? null : (
              <div>
                {/* Editable Fields */}
                <h3 style={{ margin: '0 0 0.5rem 0', color: '#0f2d1f', fontSize: '0.9rem' }}>Edit Vendor</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.3rem', maxWidth: '100%' }}>
                  <div style={{ marginBottom: '0.2rem' }}>
                    <label style={{ display: 'block', fontSize: '0.7rem', fontWeight: 600, color: '#555', marginBottom: '0.1rem' }}>Business Name</label>
                    <input style={{ ...styles.input, width: '100%', fontSize: '0.78rem', padding: '0.3rem 0.5rem' }} value={editBusinessName} onChange={(e) => setEditBusinessName(e.target.value)} />
                  </div>
                  <div style={{ marginBottom: '0.2rem' }}>
                    <label style={{ display: 'block', fontSize: '0.7rem', fontWeight: 600, color: '#555', marginBottom: '0.1rem' }}>Contact</label>
                    <input style={{ ...styles.input, width: '100%', fontSize: '0.78rem', padding: '0.3rem 0.5rem' }} value={editContactPerson} onChange={(e) => setEditContactPerson(e.target.value)} />
                  </div>
                  <div style={{ marginBottom: '0.2rem' }}>
                    <label style={{ display: 'block', fontSize: '0.7rem', fontWeight: 600, color: '#555', marginBottom: '0.1rem' }}>Email</label>
                    <input type="email" style={{ ...styles.input, width: '100%', fontSize: '0.78rem', padding: '0.3rem 0.5rem' }} value={editEmail} onChange={(e) => setEditEmail(e.target.value)} />
                  </div>
                  <div style={{ marginBottom: '0.2rem' }}>
                    <label style={{ display: 'block', fontSize: '0.7rem', fontWeight: 600, color: '#555', marginBottom: '0.1rem' }}>Phone</label>
                    <input style={{ ...styles.input, width: '100%', fontSize: '0.78rem', padding: '0.3rem 0.5rem' }} value={editPhone} onChange={(e) => setEditPhone(e.target.value)} />
                  </div>
                  <div style={{ marginBottom: '0.2rem' }}>
                    <label style={{ display: 'block', fontSize: '0.7rem', fontWeight: 600, color: '#555', marginBottom: '0.1rem' }}>Address</label>
                    <input style={{ ...styles.input, width: '100%', fontSize: '0.78rem', padding: '0.3rem 0.5rem' }} value={editMailingAddress} onChange={(e) => setEditMailingAddress(e.target.value)} />
                  </div>
                  <div style={{ marginBottom: '0.2rem' }}>
                    <label style={{ display: 'block', fontSize: '0.7rem', fontWeight: 600, color: '#555', marginBottom: '0.1rem' }}>Category</label>
                    <select style={{ ...styles.select, width: '100%', fontSize: '0.78rem', padding: '0.3rem 0.5rem' }} value={editCategory} onChange={(e) => setEditCategory(e.target.value)}>
                      <option value="">No category</option>
                      {VENDOR_CATEGORIES.map((cat) => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div style={{ marginBottom: '0.75rem', marginTop: '0.5rem' }}>
                  {(canEdit || isOrganizer) && <button style={{ ...styles.btn, fontSize: '0.75rem', padding: '0.3rem 0.6rem' }} onClick={handleSaveVendor}>Save</button>}
                  {(canEdit || isOrganizer) && <button style={{ ...styles.btnDanger, fontSize: '0.75rem', padding: '0.3rem 0.6rem' }} onClick={handleDeleteVendor}>Delete</button>}
                </div>

                {/* Event Registrations */}
                <div style={{ marginBottom: '1rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <h4 style={{ margin: 0, color: '#0f2d1f', fontSize: '0.85rem' }}>Event Registrations</h4>
                    {(canEdit || isOrganizer) && (
                      <button
                        style={{ ...styles.btn, fontSize: '0.75rem', padding: '0.3rem 0.6rem' }}
                        onClick={() => setShowRegistrationForm(!showRegistrationForm)}
                      >
                        + Add Registration
                      </button>
                    )}
                  </div>

                  {/* Add Registration Form */}
                  {showRegistrationForm && (
                    <form onSubmit={handleAddRegistration} style={{ padding: '0.75rem', backgroundColor: '#f0f0f0', borderRadius: '4px', marginBottom: '0.75rem' }}>
                      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end' }}>
                        <div>
                          <label style={{ ...styles.label, fontSize: '0.75rem' }}>Event</label>
                          <select style={styles.select} value={regEventId} onChange={(e) => setRegEventId(e.target.value)}>
                            <option value="">Select event...</option>
                            {events.map((ev) => (
                              <option key={ev.id} value={ev.id}>{ev.name}</option>
                            ))}
                          </select>
                        </div>
                        <button type="submit" style={{ ...styles.btn, marginBottom: 0 }}>Register</button>
                      </div>
                    </form>
                  )}

                  {/* Registrations List */}
                  {vendorDetail.eventRegistrations.length === 0 ? (
                    <p style={{ color: '#666', fontSize: '0.85rem' }}>No event registrations.</p>
                  ) : (
                    <table style={{ ...styles.table, fontSize: '0.8rem' }}>
                      <thead>
                        <tr>
                          <th style={styles.th}>Event</th>
                          <th style={styles.th}>Date</th>
                          <th style={styles.th}>Status</th>
                          <th style={styles.th}>Booth #</th>
                          <th style={styles.th}>Zone</th>
                          <th style={styles.th}>Description</th>
                          {isOrganizer && <th style={styles.th}></th>}
                        </tr>
                      </thead>
                      <tbody>
                        {vendorDetail.eventRegistrations.map((reg) => {
                          const boothValues = getBoothEditValues(reg);
                          return (
                            <tr key={reg.id}>
                              <td style={styles.td}>{reg.event.name}</td>
                              <td style={styles.td}>{new Date(reg.registrationDate).toLocaleDateString()}</td>
                              <td style={styles.td}>
                                <select
                                  style={{ ...styles.select, fontSize: '0.8rem', padding: '0.25rem 0.5rem' }}
                                  value={reg.status}
                                  onChange={(e) => handleUpdateRegistrationStatus(reg.id, e.target.value)}
                                >
                                  <option value="Pending">Pending</option>
                                  <option value="Confirmed">Confirmed</option>
                                  <option value="Cancelled">Cancelled</option>
                                </select>
                              </td>
                              {isOrganizer ? (
                                <>
                                  <td style={styles.td}>
                                    <input
                                      style={{ ...styles.input, width: '80px', fontSize: '0.8rem' }}
                                      value={boothValues.boothNumber}
                                      onChange={(e) => updateBoothEdit(reg.id, 'boothNumber', e.target.value)}
                                      placeholder="Booth #"
                                    />
                                  </td>
                                  <td style={styles.td}>
                                    <input
                                      style={{ ...styles.input, width: '100px', fontSize: '0.8rem' }}
                                      value={boothValues.zone}
                                      onChange={(e) => updateBoothEdit(reg.id, 'zone', e.target.value)}
                                      placeholder="Zone"
                                    />
                                  </td>
                                  <td style={styles.td}>
                                    <textarea
                                      style={{ ...styles.input, width: '150px', fontSize: '0.8rem', resize: 'vertical' as const, minHeight: '30px' }}
                                      value={boothValues.description}
                                      onChange={(e) => updateBoothEdit(reg.id, 'description', e.target.value)}
                                      placeholder="Description"
                                    />
                                  </td>
                                  <td style={styles.td}>
                                    <button
                                      style={{ ...styles.btn, fontSize: '0.7rem', padding: '0.25rem 0.5rem' }}
                                      onClick={() => handleSaveBooth(reg.id)}
                                      disabled={boothSaving === reg.id}
                                    >
                                      {boothSaving === reg.id ? 'Saving...' : 'Save Booth'}
                                    </button>
                                  </td>
                                </>
                              ) : (
                                <>
                                  <td style={styles.td}>{reg.boothNumber || '—'}</td>
                                  <td style={styles.td}>{reg.zone || '—'}</td>
                                  <td style={styles.td}>{reg.description || '—'}</td>
                                </>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>

                {/* Payments */}
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <h4 style={{ margin: 0, color: '#0f2d1f', fontSize: '0.85rem' }}>
                      Payments <span style={{ color: '#1a6b3c', fontWeight: 700 }}>({formatCurrency(vendorPaymentTotal)})</span>
                    </h4>
                    {canEdit && (
                      <button
                        style={{ ...styles.btn, fontSize: '0.75rem', padding: '0.3rem 0.6rem' }}
                        onClick={() => setShowPaymentForm(!showPaymentForm)}
                      >
                        + Add Payment
                      </button>
                    )}
                  </div>

                  {/* Add Payment Form */}
                  {showPaymentForm && (
                    <form onSubmit={handleAddPayment} style={{ padding: '0.75rem', backgroundColor: '#f0f0f0', borderRadius: '4px', marginBottom: '0.75rem' }}>
                      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                        <div>
                          <label style={{ ...styles.label, fontSize: '0.75rem' }}>Registration</label>
                          <select style={styles.select} value={payRegistrationId} onChange={(e) => setPayRegistrationId(e.target.value)}>
                            <option value="">Select registration...</option>
                            {vendorDetail.eventRegistrations.map((reg) => (
                              <option key={reg.id} value={reg.id}>{reg.event.name}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label style={{ ...styles.label, fontSize: '0.75rem' }}>Amount</label>
                          <input type="number" step="0.01" min="0.01" style={{ ...styles.input, width: '120px' }} value={payAmount} onChange={(e) => setPayAmount(e.target.value)} />
                        </div>
                        <div>
                          <label style={{ ...styles.label, fontSize: '0.75rem' }}>Date</label>
                          <input type="date" style={styles.input} value={payDate} onChange={(e) => setPayDate(e.target.value)} />
                        </div>
                        <div>
                          <label style={{ ...styles.label, fontSize: '0.75rem' }}>Method</label>
                          <select style={styles.select} value={payMethod} onChange={(e) => setPayMethod(e.target.value as PaymentMethod)}>
                            {PAYMENT_METHODS.map((m) => (
                              <option key={m} value={m}>{m}</option>
                            ))}
                          </select>
                        </div>
                        <button type="submit" style={{ ...styles.btn, marginBottom: 0 }}>Record Payment</button>
                      </div>
                    </form>
                  )}

                  {/* Payments List */}
                  {vendorDetail.eventRegistrations.every((reg) => reg.payments.length === 0) ? (
                    <p style={{ color: '#666', fontSize: '0.85rem' }}>No payments recorded.</p>
                  ) : (
                    <table style={{ ...styles.table, fontSize: '0.8rem' }}>
                      <thead>
                        <tr>
                          <th style={styles.th}>Event</th>
                          <th style={styles.th}>Amount</th>
                          <th style={styles.th}>Date</th>
                          <th style={styles.th}>Method</th>
                        </tr>
                      </thead>
                      <tbody>
                        {vendorDetail.eventRegistrations.flatMap((reg) =>
                          reg.payments.map((payment) => (
                            <tr key={payment.id}>
                              <td style={styles.td}>{reg.event.name}</td>
                              <td style={{ ...styles.td, fontWeight: 600, color: '#1a6b3c' }}>{formatCurrency(Number(payment.amount))}</td>
                              <td style={styles.td}>{new Date(payment.paymentDate).toLocaleDateString()}</td>
                              <td style={styles.td}>{payment.paymentMethod}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Pane 3: Catalog Items */}
          <div style={{ width: '300px', minWidth: '300px', backgroundColor: '#f9fafb', overflowY: 'auto', padding: '0.75rem' }}>
            {!expandedVendorId ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#6b7280', fontSize: '0.9rem' }}>
                Select a vendor to view catalog
              </div>
            ) : (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <h4 style={{ margin: 0, color: '#1a6b3c', fontSize: '0.9rem', fontWeight: 700 }}>
                    Catalog <span style={{ fontWeight: 400, color: '#6b7280' }}>({catalogItems.length})</span>
                  </h4>
                  {(canEdit || isOrganizer) && (
                    <button
                      style={{ ...styles.btn, fontSize: '0.7rem', padding: '0.25rem 0.5rem' }}
                      onClick={() => { resetCatalogForm(); setShowCatalogForm(!showCatalogForm); }}
                    >
                      +
                    </button>
                  )}
                </div>
                {/* Card/Table toggle */}
                <div style={{ display: 'flex', gap: '0.25rem', marginBottom: '0.5rem' }}>
                  <button
                    onClick={() => setCatalogViewMode('cards')}
                    style={{ flex: 1, padding: '0.25rem', fontSize: '0.7rem', fontWeight: 600, border: 'none', borderRadius: '4px', cursor: 'pointer', backgroundColor: catalogViewMode === 'cards' ? '#1a6b3c' : '#e5e7eb', color: catalogViewMode === 'cards' ? '#fff' : '#333' }}
                  >
                    Cards
                  </button>
                  <button
                    onClick={() => setCatalogViewMode('table')}
                    style={{ flex: 1, padding: '0.25rem', fontSize: '0.7rem', fontWeight: 600, border: 'none', borderRadius: '4px', cursor: 'pointer', backgroundColor: catalogViewMode === 'table' ? '#1a6b3c' : '#e5e7eb', color: catalogViewMode === 'table' ? '#fff' : '#333' }}
                  >
                    Table
                  </button>
                </div>

                {/* Add/Edit Catalog Item Form */}
                {showCatalogForm && (
                  <form onSubmit={handleSaveCatalogItem} style={{ padding: '0.75rem', backgroundColor: '#f5f7f6', borderRadius: '6px', marginBottom: '0.75rem', border: '1px solid #e5e7eb' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '0.5rem', marginBottom: '0.5rem' }}>
                      <div>
                        <label style={{ ...styles.label, fontSize: '0.75rem' }}>Name *</label>
                        <input style={{ ...styles.input, width: '100%' }} value={catName} onChange={(e) => setCatName(e.target.value)} placeholder="Item name" required />
                      </div>
                      <div>
                        <label style={{ ...styles.label, fontSize: '0.75rem' }}>Price</label>
                        <input type="number" step="0.01" min="0" style={{ ...styles.input, width: '100%' }} value={catPrice} onChange={(e) => setCatPrice(e.target.value)} placeholder="0.00" />
                      </div>
                      <div>
                        <label style={{ ...styles.label, fontSize: '0.75rem' }}>Category</label>
                        <select style={{ ...styles.select, width: '100%' }} value={catCategory} onChange={(e) => setCatCategory(e.target.value)}>
                          <option value="">Select...</option>
                          {VENDOR_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </div>
                      <div>
                        <label style={{ ...styles.label, fontSize: '0.75rem' }}>Tags (comma-separated)</label>
                        <input style={{ ...styles.input, width: '100%' }} value={catTags} onChange={(e) => setCatTags(e.target.value)} placeholder="e.g. spicy, rice, plate" />
                      </div>
                      <div>
                        <label style={{ ...styles.label, fontSize: '0.75rem' }}>Description</label>
                        <textarea style={{ ...styles.input, width: '100%', minHeight: '50px', resize: 'vertical' as const }} value={catDescription} onChange={(e) => setCatDescription(e.target.value)} placeholder="Brief description" />
                      </div>
                      <div>
                        <label style={{ ...styles.label, fontSize: '0.75rem' }}>Image (max 5MB)</label>
                        <input type="file" accept="image/*" onChange={handleCatalogImageUpload} style={{ fontSize: '0.75rem' }} />
                        {catImageUploading && <span style={{ fontSize: '0.7rem', color: '#1a6b3c' }}>Uploading...</span>}
                        {catImageUrl && <span style={{ fontSize: '0.7rem', color: '#2e7d32' }}>✓ Image attached</span>}
                      </div>
                      <div>
                        <label style={{ ...styles.label, fontSize: '0.75rem' }}>Video Link (YouTube/Instagram)</label>
                        <input style={{ ...styles.input, width: '100%' }} value={catVideoUrl} onChange={(e) => setCatVideoUrl(e.target.value)} placeholder="https://youtube.com/..." />
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                        <input type="checkbox" id="cat-featured-desktop" checked={catFeatured} onChange={(e) => setCatFeatured(e.target.checked)} />
                        <label htmlFor="cat-featured-desktop" style={{ fontSize: '0.8rem', color: '#333' }}>Featured item</label>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button type="submit" style={{ ...styles.btn, marginBottom: 0 }}>{catalogEditId ? 'Update' : 'Add Item'}</button>
                      <button type="button" style={{ ...styles.toggleInactive, marginBottom: 0 }} onClick={resetCatalogForm}>Cancel</button>
                    </div>
                  </form>
                )}

                {/* Catalog Items Grid/Table */}
                {catalogLoading ? (
                  <p style={{ color: '#6b7280', fontSize: '0.8rem' }}>Loading...</p>
                ) : catalogItems.length === 0 ? (
                  <p style={{ color: '#6b7280', fontSize: '0.8rem', margin: 0 }}>No items yet.</p>
                ) : catalogViewMode === 'cards' ? (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '0.5rem' }}>
                    {catalogItems.map((item) => (
                      <div key={item.id} style={{ padding: '0.5rem', backgroundColor: '#fff', borderRadius: '6px', border: '1px solid #e5e7eb', position: 'relative' }}>
                        {item.imageUrl && (
                          <img
                            src={item.imageUrl.startsWith('http') ? item.imageUrl : `${API_BASE}${item.imageUrl}`}
                            alt={item.name}
                            style={{ width: '100%', height: 'auto', maxHeight: '120px', objectFit: 'contain', borderRadius: '4px', marginBottom: '0.3rem', backgroundColor: '#f9fafb' }}
                          />
                        )}
                        <div style={{ fontWeight: 600, fontSize: '0.78rem', color: '#0f2d1f' }}>{item.name}</div>
                        {item.price !== null && Number(item.price) > 0 && <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#c9a84c' }}>${Number(item.price).toFixed(2)}</div>}
                        {item.featured && <span style={{ position: 'absolute', top: '3px', right: '3px', fontSize: '0.6rem', backgroundColor: '#c9a84c', color: '#fff', padding: '1px 4px', borderRadius: '3px' }}>⭐</span>}
                        {(canEdit || isOrganizer) && (
                          <div style={{ display: 'flex', gap: '0.2rem', marginTop: '0.3rem' }}>
                            <button style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '1px 3px', fontSize: '0.7rem' }} onClick={() => startEditCatalogItem(item)} title="Edit">✏️</button>
                            <button style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '1px 3px', fontSize: '0.7rem' }} onClick={() => handleDeleteCatalogItem(item.id)} title="Delete">🗑️</button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.72rem' }}>
                    <thead>
                      <tr>
                        <th style={{ textAlign: 'left', padding: '0.3rem 0.2rem', borderBottom: '1px solid #e5e7eb', fontWeight: 600, color: '#0f2d1f' }}>Name</th>
                        <th style={{ textAlign: 'right', padding: '0.3rem 0.2rem', borderBottom: '1px solid #e5e7eb', fontWeight: 600, color: '#0f2d1f' }}>Price</th>
                        <th style={{ textAlign: 'center', padding: '0.3rem 0.2rem', borderBottom: '1px solid #e5e7eb', fontWeight: 600, color: '#0f2d1f' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {catalogItems.map((item) => (
                        <tr key={item.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                          <td style={{ padding: '0.3rem 0.2rem', color: '#0f2d1f' }}>
                            {item.featured && <span>⭐ </span>}{item.name}
                          </td>
                          <td style={{ padding: '0.3rem 0.2rem', textAlign: 'right', color: '#c9a84c', fontWeight: 600 }}>
                            {item.price !== null && Number(item.price) > 0 ? `$${Number(item.price).toFixed(2)}` : ''}
                          </td>
                          <td style={{ padding: '0.3rem 0.2rem', textAlign: 'center' }}>
                            {(canEdit || isOrganizer) && (
                              <>
                                <button style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.7rem' }} onClick={() => startEditCatalogItem(item)}>✏️</button>
                                <button style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.7rem' }} onClick={() => handleDeleteCatalogItem(item.id)}>🗑️</button>
                              </>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Mobile Layout (existing stacked layout) ───────────────────

  return (
    <div style={styles.page}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1rem' }}>
        <h1 style={{ ...styles.title, marginBottom: 0 }}>Vendor Management</h1>
        <button
          style={{ ...styles.btnGold, whiteSpace: 'nowrap' }}
          onClick={handlePrintVendors}
          disabled={sortedVendors.length === 0}
          title="Print the vendor list"
          aria-label="Print vendor list"
        >
          🖨️ Print
        </button>
      </div>

      {/* Error Banner */}
      {error && (
        <div style={styles.error} onClick={() => setError('')}>
          {error} <span style={{ float: 'right', fontWeight: 700 }}>×</span>
        </div>
      )}

      {/* Revenue Summary Card */}
      <div style={styles.revenueCard}>
        <div style={styles.revenueAmount}>{formatCurrency(totalRevenue)}</div>
        <div style={styles.revenueLabel}>Total Vendor Revenue</div>
      </div>

      {/* Vendor Directory Section */}
      <div style={styles.section}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <h2 style={{ ...styles.sectionTitle, marginBottom: 0, borderBottom: 'none', paddingBottom: 0 }}>Vendor Directory</h2>
            <button
              style={viewMode === 'cards' ? styles.toggleActive : styles.toggleInactive}
              onClick={() => setViewMode('cards')}
            >
              Cards
            </button>
            <button
              style={viewMode === 'table' ? styles.toggleActive : styles.toggleInactive}
              onClick={() => setViewMode('table')}
            >
              Table
            </button>
            <button
              style={{ ...styles.toggleInactive, marginLeft: '0.75rem' }}
              onClick={handlePrintVendors}
              disabled={sortedVendors.length === 0}
              title="Print the vendor list"
              aria-label="Print vendor list"
            >
              🖨️ Print
            </button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            {/* Event Filter */}
            <select
              style={styles.select}
              value={eventFilter}
              onChange={(e) => setEventFilter(e.target.value)}
            >
              <option value="">All Events</option>
              {events.map((ev) => (
                <option key={ev.id} value={ev.id}>{ev.name}</option>
              ))}
            </select>
            {/* Category Filter */}
            <select
              style={styles.select}
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <option value="">All Categories</option>
              {VENDOR_CATEGORIES.map((cat) => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
            {/* Add Vendor Button */}
            {(canEdit || isOrganizer) && (
            <button style={styles.btn} onClick={() => setShowOnboardingForm(!showOnboardingForm)}>
              + Add Vendor
            </button>
            )}
          </div>
        </div>

        {/* Onboarding Form */}
        {showOnboardingForm && (
          <form onSubmit={handleCreateVendor} style={{ padding: '1rem', backgroundColor: '#f9f9f9', borderRadius: '6px', marginBottom: '1rem', border: '1px solid #e0e0e0' }}>
            <h3 style={{ margin: '0 0 1rem 0', color: '#0f2d1f', fontSize: '1rem' }}>New Vendor</h3>
            <div style={styles.formGrid}>
              <div style={styles.field}>
                <label style={styles.label}>Business Name *</label>
                <input style={{ ...styles.input, width: '100%' }} value={formBusinessName} onChange={(e) => setFormBusinessName(e.target.value)} />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Contact Person *</label>
                <input style={{ ...styles.input, width: '100%' }} value={formContactPerson} onChange={(e) => setFormContactPerson(e.target.value)} />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Email *</label>
                <input type="email" style={{ ...styles.input, width: '100%' }} value={formEmail} onChange={(e) => setFormEmail(e.target.value)} />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Phone</label>
                <input style={{ ...styles.input, width: '100%' }} value={formPhone} onChange={(e) => setFormPhone(e.target.value)} />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Mailing Address</label>
                <input style={{ ...styles.input, width: '100%' }} value={formMailingAddress} onChange={(e) => setFormMailingAddress(e.target.value)} />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Category</label>
                <select style={{ ...styles.select, width: '100%' }} value={formCategory} onChange={(e) => setFormCategory(e.target.value)}>
                  <option value="">Select category...</option>
                  {VENDOR_CATEGORIES.map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
            </div>
            <div style={{ marginTop: '0.5rem' }}>
              <button type="submit" style={styles.btn}>Create Vendor</button>
              <button type="button" style={styles.btnGold} onClick={() => setShowOnboardingForm(false)}>Cancel</button>
            </div>
          </form>
        )}

        {/* Vendor List */}
        {sortedVendors.length === 0 ? (
          <p style={{ color: '#666' }}>No vendors available.</p>
        ) : viewMode === 'cards' ? (
          /* Card View */
          sortedVendors.map((vendor) => (
            <React.Fragment key={vendor.id}>
              <div
                style={{
                  ...styles.card,
                  ...(expandedVendorId === vendor.id ? styles.cardExpanded : {}),
                }}
                onClick={() => handleVendorClick(vendor.id)}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <strong style={{ color: '#0f2d1f', fontSize: '1rem' }}>{vendor.businessName}</strong>
                    <div style={{ fontSize: '0.85rem', color: '#444', marginTop: '0.25rem' }}>
                      {vendor.contactPerson} · {vendor.email}
                    </div>
                    {vendor.phone && (
                      <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '0.15rem' }}>{vendor.phone}</div>
                    )}
                    {vendor.eventRegistrations && vendor.eventRegistrations.length > 0 && (
                      <div style={{ fontSize: '0.8rem', color: '#555', marginTop: '0.35rem', display: 'flex', flexWrap: 'wrap', gap: '0.35rem' }}>
                        {vendor.eventRegistrations.map((reg) => (
                          <span key={reg.id} style={{ display: 'inline-block', padding: '0.15rem 0.5rem', borderRadius: '3px', fontSize: '0.7rem', backgroundColor: '#e8f5e9', color: '#2e7d32', border: '1px solid #c8e6c9' }}>
                            {reg.event.name}{reg.boothNumber ? ` · Booth ${reg.boothNumber}` : ''}{reg.zone ? ` · ${reg.zone}` : ''}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  {vendor.category && (
                    <span style={{ display: 'inline-block', padding: '0.2rem 0.6rem', borderRadius: '3px', fontSize: '0.7rem', fontWeight: 600, color: '#fff', backgroundColor: '#1a6b3c' }}>
                      {vendor.category}
                    </span>
                  )}
                </div>
              </div>
              {/* Detail Panel (Card View) */}
              {expandedVendorId === vendor.id && renderDetailPanel()}
            </React.Fragment>
          ))
        ) : (
          /* Table View */
          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Business Name</th>
                  <th style={styles.th}>Contact Person</th>
                  <th style={styles.th}>Email</th>
                  <th style={styles.th}>Phone</th>
                  <th style={styles.th}>Category</th>
                  <th style={styles.th}>Events / Booth</th>
                </tr>
              </thead>
              <tbody>
                {sortedVendors.map((vendor) => (
                  <React.Fragment key={vendor.id}>
                    <tr
                      style={{
                        ...styles.trClickable,
                        ...(expandedVendorId === vendor.id ? styles.trExpanded : {}),
                      }}
                      onClick={() => handleVendorClick(vendor.id)}
                    >
                      <td style={styles.td}><strong style={{ color: '#0f2d1f' }}>{vendor.businessName}</strong></td>
                      <td style={styles.td}>{vendor.contactPerson}</td>
                      <td style={styles.td}>{vendor.email}</td>
                      <td style={styles.td}>{vendor.phone || '—'}</td>
                      <td style={styles.td}>{vendor.category || '—'}</td>
                      <td style={styles.td}>
                        {vendor.eventRegistrations && vendor.eventRegistrations.length > 0
                          ? vendor.eventRegistrations.map((reg) => (
                              <div key={reg.id} style={{ fontSize: '0.75rem', marginBottom: '0.15rem' }}>
                                {reg.event.name}{reg.boothNumber ? ` · #${reg.boothNumber}` : ''}{reg.zone ? ` · ${reg.zone}` : ''}
                              </div>
                            ))
                          : '—'}
                      </td>
                    </tr>
                    {/* Detail Panel (Table View) */}
                    {expandedVendorId === vendor.id && (
                      <tr>
                        <td colSpan={6} style={{ padding: '0.5rem' }}>
                          {renderDetailPanel()}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );

  // ── Detail Panel Renderer ─────────────────────────────────────

  function renderDetailPanel() {
    if (detailLoading) {
      return <div style={styles.detailPanel}><p style={{ color: '#666' }}>Loading vendor details...</p></div>;
    }
    if (!vendorDetail) return null;

    return (
      <div style={styles.detailPanel} onClick={(e) => e.stopPropagation()}>
        {/* Editable Fields */}
        <h3 style={{ margin: '0 0 1rem 0', color: '#0f2d1f', fontSize: '1rem' }}>Edit Vendor</h3>
        <div style={styles.formGrid}>
          <div style={styles.field}>
            <label style={styles.label}>Business Name</label>
            <input style={{ ...styles.input, width: '100%' }} value={editBusinessName} onChange={(e) => setEditBusinessName(e.target.value)} />
          </div>
          <div style={styles.field}>
            <label style={styles.label}>Contact Person</label>
            <input style={{ ...styles.input, width: '100%' }} value={editContactPerson} onChange={(e) => setEditContactPerson(e.target.value)} />
          </div>
          <div style={styles.field}>
            <label style={styles.label}>Email</label>
            <input type="email" style={{ ...styles.input, width: '100%' }} value={editEmail} onChange={(e) => setEditEmail(e.target.value)} />
          </div>
          <div style={styles.field}>
            <label style={styles.label}>Phone</label>
            <input style={{ ...styles.input, width: '100%' }} value={editPhone} onChange={(e) => setEditPhone(e.target.value)} />
          </div>
          <div style={styles.field}>
            <label style={styles.label}>Mailing Address</label>
            <input style={{ ...styles.input, width: '100%' }} value={editMailingAddress} onChange={(e) => setEditMailingAddress(e.target.value)} />
          </div>
          <div style={styles.field}>
            <label style={styles.label}>Category</label>
            <select style={{ ...styles.select, width: '100%' }} value={editCategory} onChange={(e) => setEditCategory(e.target.value)}>
              <option value="">No category</option>
              {VENDOR_CATEGORIES.map((cat) => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
        </div>
        <div style={{ marginBottom: '1.5rem' }}>
          {(canEdit || isOrganizer) && <button style={styles.btn} onClick={handleSaveVendor}>Save</button>}
          {(canEdit || isOrganizer) && <button style={styles.btnDanger} onClick={handleDeleteVendor}>Delete</button>}
        </div>

        {/* Catalog Items — PRIMARY SECTION */}
        <div style={{ marginBottom: '1.5rem', padding: '1rem', backgroundColor: '#f0f7f2', borderRadius: '8px', border: '1px solid #c8e6c9' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
            <h4 style={{ margin: 0, color: '#1a6b3c', fontSize: '1rem', fontWeight: 700 }}>
              🏪 Catalog Items <span style={{ fontWeight: 400, color: '#6b7280' }}>({catalogItems.length})</span>
            </h4>
            {(canEdit || isOrganizer) && (
              <button
                style={{ ...styles.btn, fontSize: '0.8rem', padding: '0.4rem 0.75rem' }}
                onClick={() => { resetCatalogForm(); setShowCatalogForm(!showCatalogForm); }}
              >
                + Add Item
              </button>
            )}
          </div>

          {/* Add/Edit Catalog Item Form */}
          {showCatalogForm && (
            <form onSubmit={handleSaveCatalogItem} style={{ padding: '0.75rem', backgroundColor: '#fff', borderRadius: '6px', marginBottom: '0.75rem', border: '1px solid #e0e0e0' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <div>
                  <label style={{ ...styles.label, fontSize: '0.75rem' }}>Name *</label>
                  <input style={{ ...styles.input, width: '100%' }} value={catName} onChange={(e) => setCatName(e.target.value)} placeholder="Item name" required />
                </div>
                <div>
                  <label style={{ ...styles.label, fontSize: '0.75rem' }}>Price</label>
                  <input type="number" step="0.01" min="0" style={{ ...styles.input, width: '100%' }} value={catPrice} onChange={(e) => setCatPrice(e.target.value)} placeholder="0.00" />
                </div>
                <div>
                  <label style={{ ...styles.label, fontSize: '0.75rem' }}>Category</label>
                  <select style={{ ...styles.select, width: '100%' }} value={catCategory} onChange={(e) => setCatCategory(e.target.value)}>
                    <option value="">Select...</option>
                    {VENDOR_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ ...styles.label, fontSize: '0.75rem' }}>Tags (comma-separated)</label>
                  <input style={{ ...styles.input, width: '100%' }} value={catTags} onChange={(e) => setCatTags(e.target.value)} placeholder="e.g. spicy, rice, plate" />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ ...styles.label, fontSize: '0.75rem' }}>Description</label>
                  <textarea style={{ ...styles.input, width: '100%', minHeight: '50px', resize: 'vertical' as const }} value={catDescription} onChange={(e) => setCatDescription(e.target.value)} placeholder="Brief description" />
                </div>
                <div>
                  <label style={{ ...styles.label, fontSize: '0.75rem' }}>Image (max 5MB)</label>
                  <input type="file" accept="image/*" onChange={handleCatalogImageUpload} style={{ fontSize: '0.75rem' }} />
                  {catImageUploading && <span style={{ fontSize: '0.7rem', color: '#1a6b3c' }}>Uploading...</span>}
                  {catImageUrl && <span style={{ fontSize: '0.7rem', color: '#2e7d32' }}>✓ Image attached</span>}
                </div>
                <div>
                  <label style={{ ...styles.label, fontSize: '0.75rem' }}>Video Link (YouTube/Instagram)</label>
                  <input style={{ ...styles.input, width: '100%' }} value={catVideoUrl} onChange={(e) => setCatVideoUrl(e.target.value)} placeholder="https://youtube.com/..." />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                  <input type="checkbox" id="cat-featured" checked={catFeatured} onChange={(e) => setCatFeatured(e.target.checked)} />
                  <label htmlFor="cat-featured" style={{ fontSize: '0.8rem', color: '#333' }}>Featured item</label>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button type="submit" style={{ ...styles.btn, marginBottom: 0 }}>{catalogEditId ? 'Update' : 'Add Item'}</button>
                <button type="button" style={{ ...styles.toggleInactive, marginBottom: 0 }} onClick={resetCatalogForm}>Cancel</button>
              </div>
            </form>
          )}

          {/* Catalog Items List */}
          {catalogLoading ? (
            <p style={{ color: '#666', fontSize: '0.85rem' }}>Loading items...</p>
          ) : catalogItems.length === 0 ? (
            <p style={{ color: '#666', fontSize: '0.85rem', margin: 0 }}>No catalog items yet. Add items for attendees to browse.</p>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '0.6rem' }}>
              {catalogItems.map((item) => (
                <div key={item.id} style={{ padding: '0.6rem', backgroundColor: '#fff', borderRadius: '8px', border: '1px solid #e0e0e0', position: 'relative' }}>
                  {item.imageUrl && (
                    <img
                      src={item.imageUrl.startsWith('http') ? item.imageUrl : `${API_BASE}${item.imageUrl}`}
                      alt={item.name}
                      style={{ width: '100%', height: 'auto', maxHeight: '120px', objectFit: 'contain', borderRadius: '6px', marginBottom: '0.4rem', backgroundColor: '#f9fafb' }}
                    />
                  )}
                  <div style={{ fontWeight: 600, fontSize: '0.85rem', color: '#0f2d1f' }}>{item.name}</div>
                  {item.price !== null && Number(item.price) > 0 && <div style={{ fontSize: '0.8rem', fontWeight: 700, color: '#c9a84c' }}>${Number(item.price).toFixed(2)}</div>}
                  {item.category && <div style={{ fontSize: '0.7rem', color: '#6b7280' }}>{item.category}</div>}
                  {item.featured && <span style={{ position: 'absolute', top: '4px', right: '4px', fontSize: '0.7rem', backgroundColor: '#c9a84c', color: '#fff', padding: '1px 5px', borderRadius: '4px' }}>⭐</span>}
                  {(canEdit || isOrganizer) && (
                    <div style={{ display: 'flex', gap: '0.3rem', marginTop: '0.4rem' }}>
                      <button style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px', fontSize: '0.8rem' }} onClick={() => startEditCatalogItem(item)} title="Edit" aria-label={`Edit ${item.name}`}>✏️</button>
                      <button style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px', fontSize: '0.8rem' }} onClick={() => handleDeleteCatalogItem(item.id)} title="Delete" aria-label={`Delete ${item.name}`}>🗑️</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Event Registrations */}
        <div style={{ marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
            <h4 style={{ margin: 0, color: '#0f2d1f', fontSize: '0.95rem' }}>Event Registrations</h4>
            {(canEdit || isOrganizer) && (
            <button
              style={{ ...styles.btn, fontSize: '0.75rem', padding: '0.3rem 0.6rem' }}
              onClick={() => setShowRegistrationForm(!showRegistrationForm)}
            >
              + Add Registration
            </button>
            )}
          </div>

          {/* Add Registration Form */}
          {showRegistrationForm && (
            <form onSubmit={handleAddRegistration} style={{ padding: '0.75rem', backgroundColor: '#f0f0f0', borderRadius: '4px', marginBottom: '0.75rem' }}>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end' }}>
                <div>
                  <label style={{ ...styles.label, fontSize: '0.75rem' }}>Event</label>
                  <select style={styles.select} value={regEventId} onChange={(e) => setRegEventId(e.target.value)}>
                    <option value="">Select event...</option>
                    {events.map((ev) => (
                      <option key={ev.id} value={ev.id}>{ev.name}</option>
                    ))}
                  </select>
                </div>
                <button type="submit" style={{ ...styles.btn, marginBottom: 0 }}>Register</button>
              </div>
            </form>
          )}

          {/* Registrations List */}
          {vendorDetail.eventRegistrations.length === 0 ? (
            <p style={{ color: '#666', fontSize: '0.85rem' }}>No event registrations.</p>
          ) : (
            <table style={{ ...styles.table, fontSize: '0.8rem' }}>
              <thead>
                <tr>
                  <th style={styles.th}>Event</th>
                  <th style={styles.th}>Date</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Booth #</th>
                  <th style={styles.th}>Zone</th>
                  <th style={styles.th}>Description</th>
                  {isOrganizer && <th style={styles.th}></th>}
                </tr>
              </thead>
              <tbody>
                {vendorDetail.eventRegistrations.map((reg) => {
                  const boothValues = getBoothEditValues(reg);
                  return (
                    <tr key={reg.id}>
                      <td style={styles.td}>{reg.event.name}</td>
                      <td style={styles.td}>{new Date(reg.registrationDate).toLocaleDateString()}</td>
                      <td style={styles.td}>
                        <select
                          style={{ ...styles.select, fontSize: '0.8rem', padding: '0.25rem 0.5rem' }}
                          value={reg.status}
                          onChange={(e) => handleUpdateRegistrationStatus(reg.id, e.target.value)}
                        >
                          <option value="Pending">Pending</option>
                          <option value="Confirmed">Confirmed</option>
                          <option value="Cancelled">Cancelled</option>
                        </select>
                      </td>
                      {isOrganizer ? (
                        <>
                          <td style={styles.td}>
                            <input
                              style={{ ...styles.input, width: '80px', fontSize: '0.8rem' }}
                              value={boothValues.boothNumber}
                              onChange={(e) => updateBoothEdit(reg.id, 'boothNumber', e.target.value)}
                              placeholder="Booth #"
                            />
                          </td>
                          <td style={styles.td}>
                            <input
                              style={{ ...styles.input, width: '100px', fontSize: '0.8rem' }}
                              value={boothValues.zone}
                              onChange={(e) => updateBoothEdit(reg.id, 'zone', e.target.value)}
                              placeholder="Zone"
                            />
                          </td>
                          <td style={styles.td}>
                            <textarea
                              style={{ ...styles.input, width: '150px', fontSize: '0.8rem', resize: 'vertical' as const, minHeight: '30px' }}
                              value={boothValues.description}
                              onChange={(e) => updateBoothEdit(reg.id, 'description', e.target.value)}
                              placeholder="Description"
                            />
                          </td>
                          <td style={styles.td}>
                            <button
                              style={{ ...styles.btn, fontSize: '0.7rem', padding: '0.25rem 0.5rem' }}
                              onClick={() => handleSaveBooth(reg.id)}
                              disabled={boothSaving === reg.id}
                            >
                              {boothSaving === reg.id ? 'Saving...' : 'Save Booth'}
                            </button>
                          </td>
                        </>
                      ) : (
                        <>
                          <td style={styles.td}>{reg.boothNumber || '—'}</td>
                          <td style={styles.td}>{reg.zone || '—'}</td>
                          <td style={styles.td}>{reg.description || '—'}</td>
                        </>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Payments */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
            <h4 style={{ margin: 0, color: '#0f2d1f', fontSize: '0.95rem' }}>
              Payments <span style={{ color: '#1a6b3c', fontWeight: 700 }}>({formatCurrency(vendorPaymentTotal)})</span>
            </h4>
            {canEdit && (
            <button
              style={{ ...styles.btn, fontSize: '0.75rem', padding: '0.3rem 0.6rem' }}
              onClick={() => setShowPaymentForm(!showPaymentForm)}
            >
              + Add Payment
            </button>
            )}
          </div>

          {/* Add Payment Form */}
          {showPaymentForm && (
            <form onSubmit={handleAddPayment} style={{ padding: '0.75rem', backgroundColor: '#f0f0f0', borderRadius: '4px', marginBottom: '0.75rem' }}>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <div>
                  <label style={{ ...styles.label, fontSize: '0.75rem' }}>Registration</label>
                  <select style={styles.select} value={payRegistrationId} onChange={(e) => setPayRegistrationId(e.target.value)}>
                    <option value="">Select registration...</option>
                    {vendorDetail.eventRegistrations.map((reg) => (
                      <option key={reg.id} value={reg.id}>{reg.event.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={{ ...styles.label, fontSize: '0.75rem' }}>Amount</label>
                  <input type="number" step="0.01" min="0.01" style={{ ...styles.input, width: '120px' }} value={payAmount} onChange={(e) => setPayAmount(e.target.value)} />
                </div>
                <div>
                  <label style={{ ...styles.label, fontSize: '0.75rem' }}>Date</label>
                  <input type="date" style={styles.input} value={payDate} onChange={(e) => setPayDate(e.target.value)} />
                </div>
                <div>
                  <label style={{ ...styles.label, fontSize: '0.75rem' }}>Method</label>
                  <select style={styles.select} value={payMethod} onChange={(e) => setPayMethod(e.target.value as PaymentMethod)}>
                    {PAYMENT_METHODS.map((m) => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </select>
                </div>
                <button type="submit" style={{ ...styles.btn, marginBottom: 0 }}>Record Payment</button>
              </div>
            </form>
          )}

          {/* Payments List */}
          {vendorDetail.eventRegistrations.every((reg) => reg.payments.length === 0) ? (
            <p style={{ color: '#666', fontSize: '0.85rem' }}>No payments recorded.</p>
          ) : (
            <table style={{ ...styles.table, fontSize: '0.8rem' }}>
              <thead>
                <tr>
                  <th style={styles.th}>Event</th>
                  <th style={styles.th}>Amount</th>
                  <th style={styles.th}>Date</th>
                  <th style={styles.th}>Method</th>
                </tr>
              </thead>
              <tbody>
                {vendorDetail.eventRegistrations.flatMap((reg) =>
                  reg.payments.map((payment) => (
                    <tr key={payment.id}>
                      <td style={styles.td}>{reg.event.name}</td>
                      <td style={{ ...styles.td, fontWeight: 600, color: '#1a6b3c' }}>{formatCurrency(Number(payment.amount))}</td>
                      <td style={styles.td}>{new Date(payment.paymentDate).toLocaleDateString()}</td>
                      <td style={styles.td}>{payment.paymentMethod}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
    );
  }
}
